import React, { useCallback, useEffect, useState } from 'react';
import { courseSupportingDataService, CourseSupportingDataItem } from '../../../../services/courseSupportingDataService';

export function CourseTextEditor({ item, onBack }: {
  item: CourseSupportingDataItem;
  onBack: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'full' | 'summary'>('full');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{item.name}</h2>
        <p className="text-sm text-gray-500 mt-1">{item.description}</p>
      </div>

      {item.has_summary && (
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('full')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'full'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Full Document
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'summary'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Summary (used by directional stages)
          </button>
        </div>
      )}

      {activeTab === 'full' ? (
        <TextEditorPane assetId={item.asset_id} mode="full" />
      ) : (
        <TextEditorPane assetId={item.asset_id} mode="summary" />
      )}

      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>
          {activeTab === 'summary'
            ? 'The summary is used by directional stages (syllabus generation). The full document is used by drafting stages that produce learner-facing prose.'
            : 'Changes affect all future course generation. This document is injected into prompts via variable substitution.'}
        </span>
      </div>
    </div>
  );
}

function TextEditorPane({ assetId, mode }: { assetId: string; mode: 'full' | 'summary' }) {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [etag, setEtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setSavedMessage(null);
      try {
        const data = mode === 'summary'
          ? await courseSupportingDataService.getSummary(assetId)
          : await courseSupportingDataService.get(assetId);
        if (cancelled) return;
        setContent(data.content);
        setOriginalContent(data.content);
        setEtag(data.etag);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assetId, mode]);

  const isDirty = content !== originalContent;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const result = mode === 'summary'
        ? await courseSupportingDataService.updateSummary(assetId, content, etag)
        : await courseSupportingDataService.update(assetId, content, etag);
      setEtag(result.etag);
      setOriginalContent(content);
      setSavedMessage('Saved successfully');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [content, etag, assetId, mode]);

  const handleDiscard = () => {
    setContent(originalContent);
    setError(null);
    setSavedMessage(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <textarea
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        rows={mode === 'summary' ? 20 : 30}
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleDiscard}
          disabled={!isDirty}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Discard changes
        </button>
        {savedMessage && <span className="text-sm text-green-600">{savedMessage}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>
    </div>
  );
}
